import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";

const GUIDES = {
  Artwork: {
    tagline: "Original acrylic canvases, dot work and contemporary pieces.",
    tiers: [
      { name: "Small original canvas", size: "up to 40 × 50 cm", price: "from $350" },
      { name: "Medium original canvas", size: "60 × 90 cm", price: "from $650" },
      { name: "Large statement canvas", size: "90 × 120 cm+", price: "from $1,200" },
      { name: "Fine art print (existing work)", size: "A4 – A0", price: "$95 – $475" },
    ],
    notes: "Turnaround 2–6 weeks for originals. Framing +$90 – $260 depending on size.",
  },
  Design: {
    tagline: "Graphic design, apparel prints and brand identity.",
    tiers: [
      { name: "Single graphic (apparel / sticker / print-ready)", size: "1 concept + 2 revisions", price: "from $180" },
      { name: "Brand mark / logo", size: "3 concepts + full brand pack", price: "from $650" },
      { name: "Full identity system", size: "logo, colour, type, socials", price: "from $1,400" },
      { name: "Digital design pack", size: "PNG / SVG / print-ready", price: "from $90" },
    ],
    notes: "All designs delivered as high-res PNG, SVG and print-ready PDF.",
  },
  "3D Printing": {
    tagline: "Object printing, prototypes and small cultural pieces.",
    tiers: [
      { name: "Small object", size: "up to 10 cm", price: "from $45" },
      { name: "Medium object", size: "up to 20 cm", price: "from $110" },
      { name: "Large / multi-part", size: "20 – 40 cm", price: "from $260" },
      { name: "Painted & finished", size: "hand-finished top coat", price: "add $60 – $180" },
    ],
    notes: "Materials: PLA / PETG. Custom colours available on request.",
  },
  Engraving: {
    tagline: "Laser wood and acrylic engraving — plaques, signs, gifts.",
    tiers: [
      { name: "Small item (keychain / coaster)", size: "up to 10 cm", price: "from $18" },
      { name: "Medium (plaque / photo frame)", size: "up to 30 cm", price: "from $65" },
      { name: "Large signage panel", size: "40 – 90 cm", price: "from $180" },
      { name: "Repeat / bulk orders", size: "10+ units", price: "10 – 25% off" },
    ],
    notes: "Supply your own timber or choose from our stock. Vector artwork preferred.",
  },
  Murals: {
    tagline: "Interior + exterior murals, home walls to full commercial exteriors.",
    tiers: [
      { name: "Small feature wall (interior)", size: "up to 6 m²", price: "from $850" },
      { name: "Medium mural", size: "6 – 20 m²", price: "$1,800 – $4,500" },
      { name: "Large exterior mural", size: "20 – 60 m²", price: "$4,500 – $12,000" },
      { name: "Signature scale project", size: "60 m²+", price: "quote on request" },
    ],
    notes: "Quotes include design, primer and clear-coat protection. Travel outside Vic/NSW additional.",
  },
  "Tattoo Design": {
    tagline: "Custom flash + full one-off tattoo designs (design only, artist not included).",
    tiers: [
      { name: "Flash-style design", size: "single motif", price: "from $120" },
      { name: "Custom full design", size: "half sleeve / back piece", price: "from $380" },
      { name: "Full sleeve concept", size: "flowing multi-motif", price: "from $720" },
      { name: "Rework / cover-up design", size: "concepting + linework", price: "from $260" },
    ],
    notes: "Delivered as high-res PNG + line-drawing PDF ready for your tattooist.",
  },
};

export default function ServiceQuoteDialog({ open, onOpenChange, service }) {
  const guide = service ? GUIDES[service] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="service-quote-dialog" className="max-w-2xl bg-[#0F0F0F] border border-white/10 text-white p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <VisuallyHidden>
          <DialogTitle>{service ? `${service} — rough price guide` : "Service price guide"}</DialogTitle>
          <DialogDescription>Rough price ranges for CKA custom services.</DialogDescription>
        </VisuallyHidden>
        <div className="sticky top-0 z-10 bg-[#0F0F0F]/95 backdrop-blur border-b border-white/10 px-6 md:px-8 py-5 flex items-center justify-between">
          <div>
            <p className="overline mb-1">Rough price guide</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-wider">{service}</h2>
          </div>
          <button data-testid="sq-close" onClick={() => onOpenChange(false)} className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#FF4500] hover:text-[#FF4500]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin px-6 md:px-8 py-6">
          {guide ? (
            <>
              <p className="text-white/70 mb-8">{guide.tagline}</p>
              <div className="space-y-3 mb-8">
                {guide.tiers.map((t, i) => (
                  <div key={i} data-testid={`sq-tier-${i}`} className="flex items-start justify-between gap-4 border border-white/10 bg-[#141414] p-4">
                    <div className="min-w-0">
                      <p className="font-display text-lg tracking-wider">{t.name}</p>
                      <p className="text-xs text-white/50 uppercase tracking-widest mt-1">{t.size}</p>
                    </div>
                    <p className="text-[#FF4500] font-semibold whitespace-nowrap">{t.price}</p>
                  </div>
                ))}
              </div>
              <div className="border-l-2 border-[#FF4500] pl-4 mb-8 text-sm text-white/60">{guide.notes}</div>
              <p className="text-xs text-white/40 mb-6">Prices are indicative only. Every project is unique — request a proper quote below and Cam will get back within 2 business days.</p>
            </>
          ) : (
            <p className="text-white/50">No guide available.</p>
          )}
        </div>
        <div className="sticky bottom-0 z-10 bg-[#0F0F0F]/95 backdrop-blur border-t border-white/10 px-6 md:px-8 py-4 flex items-center justify-end gap-3">
          <button onClick={() => onOpenChange(false)} className="btn-ghost text-sm py-2 px-5">Close</button>
          <Link
            to="/custom"
            onClick={() => onOpenChange(false)}
            data-testid="sq-request-quote"
            className="btn-primary text-sm py-2 px-6"
          >
            Request a quote
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
